import { ShoppingCart } from '@mui/icons-material';
import { Button, Paper, Stack, Typography } from '@mui/material';
import { Price } from 'apis/price';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StateType } from 'store/reducers';
import { getErrorMessage } from 'utils/errors';
import { localizePrice } from 'utils/numbers';
import { Payment } from 'utils/payments';
import { get as getPaymentData, makePayment } from '../../../apis/payments';
import { usePaymentRedirect } from '@01bee-npm-registry/worldline-react';

export interface PaymentOptionProps {
    description?: string;
    price: number;
    discountedPrice?: number;
    vattedPrice: number;
    vat: number;
    months: number;
    priceList: Price;
    setError?: Dispatch<SetStateAction<string | undefined>>;
}

const PaymentOption: React.FC<PaymentOptionProps> = ({
    description,
    price = 0,
    discountedPrice,
    vattedPrice = 0,
    vat = 0,
    months = 1,
    setError,
    priceList
}): JSX.Element => {
    const { t } = useTranslation();
    const { accessToken, agendaId } = useSelector((state: StateType) => state.auth);

    const amount = useMemo(() => Math.max(discountedPrice ? discountedPrice : price, 0), [discountedPrice, price]);
    const years = useMemo(() => Math.floor(months / 12), [months]);
    const yearMonths = useMemo(() => months % 12, [months]);

    const { trigger } = usePaymentRedirect({
        endpoint: `${process.env.REACT_APP_SERVER_URL || 'http://localhost:8080'}/agenda/${agendaId ?? ''}/payments/worldline/new-payment`,
        licenseId: process.env.REACT_APP_WORLDLINE_LICENSE_ID ?? '',
        worldlineURL: process.env.REACT_APP_WORLDLINE_URL ?? '',
        headers: {
            Authorization: `Bearer ${accessToken ?? ''}`
        },
        body: {
            months,
            urlDone: `${process.env.REACT_APP_URL ?? 'http://localhost:3000'}/license/renew?AMOUNT=${
                vattedPrice ?? discountedPrice ?? price
            }`,
            urlError: `${process.env.REACT_APP_URL ?? 'http://localhost:3000'}/license/renew`
        }
    });

    const handleClick = () => {
        if (accessToken && agendaId) {
            trigger().catch((error) => console.error(error));
        }
    };

    return (
        <Paper sx={{ height: '100%' }}>
            <Stack p={2} spacing={2} justifyContent="space-between" height="100%">
                <Stack direction="row" alignItems="baseline">
                    <Typography variant="h3">+</Typography>
                    {!!years && (
                        <Typography variant="h3" mx={0.5}>
                            {years} {t('year', { count: years })}
                        </Typography>
                    )}
                    {!!years && !!yearMonths && (
                        <Typography variant="h3" mx={1}>
                            {t('x_and_y')}
                        </Typography>
                    )}
                    {!!yearMonths && (
                        <Typography variant="h3" mx={0.5}>
                            {yearMonths} {t('month', { count: yearMonths })}
                        </Typography>
                    )}
                </Stack>
                <Stack justifyContent="end">
                    <Typography variant="h6" color="textSecondary" my={1} whiteSpace="pre-line">
                        {discountedPrice &&
                            discountedPrice < price &&
                            priceList.discount?.find((row) => row.months === months)?.description?.it}
                    </Typography>
                    <Stack alignItems="end">
                        <Stack direction="row" alignItems="baseline" justifyContent="end" spacing={2} style={{ marginBottom: '8px' }}>
                            {discountedPrice && discountedPrice < price && (
                                <Typography variant="h6" color="textSecondary" style={{ textDecoration: 'line-through', margin: 0 }} my={1}>
                                    {localizePrice(price)} €
                                </Typography>
                            )}
                            <Typography variant="h4">{localizePrice(amount)} €</Typography>
                        </Stack>
                        <Typography variant="h6">
                            +{t('vat')?.toUpperCase() || ''} {vat} %
                        </Typography>
                        <Typography variant="h3" marginBottom={2}>
                            {t('total')}: {localizePrice(vattedPrice)} €
                        </Typography>
                    </Stack>
                    <Button variant="outlined" startIcon={<ShoppingCart />} onClick={handleClick}>
                        {t('license_renew_action')}
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    );
};

export default PaymentOption;
